import { PDFDocument } from 'pdf-lib';

export const DIRECT_UPLOAD_MAX_FILES = 10;
export const BULK_UPLOAD_MAX_FILES = 200;
export const MIGRATION_MAX_FILES = 2000;

export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
export const MAX_PDF_PAGES = 5;

export const ACCEPTED_EXTENSIONS = ['.pdf', '.docx'] as const;
export const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;

export type UploadValidationReason =
  | 'unsupported_file_type'
  | 'file_too_large'
  | 'too_many_pages';

export type UploadValidationIssue = {
  fileName: string;
  fileType: string;
  extension: string;
  sizeBytes: number;
  pageCount?: number;
  reason: UploadValidationReason;
  message: string;
  allowedExtensions: readonly string[];
  maxFileSizeBytes: number;
  maxPages?: number;
};

export type UploadValidationResult = {
  validFiles: File[];
  issues: UploadValidationIssue[];
};

export function toMb(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(2);
}

export function getFileExtension(fileName: string): string {
  const idx = fileName.lastIndexOf('.');
  if (idx < 0) return '';
  return fileName.slice(idx).toLowerCase();
}

export async function validateResumeFiles(files: File[]): Promise<UploadValidationResult> {
  const validFiles: File[] = [];
  const issues: UploadValidationIssue[] = [];

  for (const file of files) {
    const extension = getFileExtension(file.name);
    const mime = (file.type || '').toLowerCase();
    const typeAllowed =
      ACCEPTED_MIME_TYPES.includes(mime as (typeof ACCEPTED_MIME_TYPES)[number]) ||
      ACCEPTED_EXTENSIONS.includes(extension as (typeof ACCEPTED_EXTENSIONS)[number]);

    if (!typeAllowed) {
      issues.push({
        fileName: file.name,
        fileType: file.type || 'unknown',
        extension,
        sizeBytes: file.size,
        reason: 'unsupported_file_type',
        message: `Unsupported file type. Uploaded: ${file.type || extension || 'unknown'}. Supported: PDF, DOCX.`,
        allowedExtensions: ACCEPTED_EXTENSIONS,
        maxFileSizeBytes: MAX_FILE_SIZE_BYTES,
      });
      continue;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      issues.push({
        fileName: file.name,
        fileType: file.type || 'unknown',
        extension,
        sizeBytes: file.size,
        reason: 'file_too_large',
        message: `File too large. Uploaded: ${toMb(file.size)} MB. Max allowed: ${toMb(MAX_FILE_SIZE_BYTES)} MB.`,
        allowedExtensions: ACCEPTED_EXTENSIONS,
        maxFileSizeBytes: MAX_FILE_SIZE_BYTES,
      });
      continue;
    }

    // Check page count for PDF files. Use extension OR MIME so PDFs with missing MIME don't bypass the page gate.
    if (extension === '.pdf' || mime === 'application/pdf') {
      try {
        const pageCount = await countPdfPages(file);
        if (pageCount > MAX_PDF_PAGES) {
          issues.push({
            fileName: file.name,
            fileType: file.type || 'unknown',
            extension,
            sizeBytes: file.size,
            pageCount,
            reason: 'too_many_pages',
            message: `Resume too long. Uploaded: ${pageCount} pages. Max allowed: ${MAX_PDF_PAGES} pages.`,
            allowedExtensions: ACCEPTED_EXTENSIONS,
            maxFileSizeBytes: MAX_FILE_SIZE_BYTES,
            maxPages: MAX_PDF_PAGES,
          });
          continue;
        }
      } catch (error) {
        // If page count can't be determined, reject the file to be safe
        console.error(`Failed to validate page count for ${file.name}:`, error);
        issues.push({
          fileName: file.name,
          fileType: file.type || 'unknown',
          extension,
          sizeBytes: file.size,
          reason: 'too_many_pages',
          message: `Could not validate page count. Please try again.`,
          allowedExtensions: ACCEPTED_EXTENSIONS,
          maxFileSizeBytes: MAX_FILE_SIZE_BYTES,
          maxPages: MAX_PDF_PAGES,
        });
        continue;
      }
    }

    validFiles.push(file);
  }

  return { validFiles, issues };
}

async function countPdfPages(file: File): Promise<number> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    return pdfDoc.getPageCount();
  } catch (error) {
    console.error(`Failed to count PDF pages for ${file.name}:`, error);
    throw error;
  }
}

export function issuesToTooltip(issues: UploadValidationIssue[]): string {
  return issues
    .map(issue => `${issue.fileName}: ${issue.message}`)
    .join('\n');
}

export const UPLOAD_VALIDATION_REASONS: Record<UploadValidationReason, string> = {
  'unsupported_file_type': 'Unsupported file type. Supported: PDF, DOCX.',
  'file_too_large': `File size exceeds maximum of ${toMb(MAX_FILE_SIZE_BYTES)} MB.`,
  'too_many_pages': `Resume exceeds maximum of ${MAX_PDF_PAGES} pages.`,
};
